// src/components/DdosTesting/AttackConfiguration.jsx
import React, { useEffect, useState } from 'react';

// Updated props
const AttackConfiguration = ({
  attackConfig,
  onConfigChange,
  onStartAttack,
  onStopAttack,
  isAttackRunning,
  isLoading, // Loading state for start/stop actions
  isDisabled // Overall disabled state (e.g., no server connection)
}) => {
  // Local state to manage temporary input values to avoid prop drilling on every keystroke
  // Sync local state when the main config prop changes
  const [localConfig, setLocalConfig] = useState(attackConfig);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
      setLocalConfig(attackConfig);
  }, [attackConfig]);

  // Handle input changes locally
  const handleInputChange = (field, value) => {
      // Ensure threads and intensity are stored as numbers if they come from range inputs
      // However, range input values are strings, so parseInt will be needed when passing to parent.
      // For local state, string is fine as input value expects string.
      setLocalConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleDurationChange = (field, value) => {
       const numericValue = field === 'value' ? parseInt(value, 10) || 0 : value;
       setLocalConfig(prev => ({
           ...prev,
           duration: {
               ...prev.duration,
               [field]: numericValue
           }
       }));
   };

  // Update parent state only on blur or specific actions
  const handleBlur = (field, value) => {
      // 'intensity' and 'threads' are now handled by onMouseUp/onTouchEnd for sliders.
      // 'method' and 'durationUnit' are handled by their own onChange for immediate parent update.
      if (field === 'durationValue') {
           onConfigChange({ duration: { ...attackConfig.duration, value: parseInt(value, 10) || 0 } });
      }
      // No longer needed for 'method' as it updates on select change.
      // else if (field === 'method') {
      //      onConfigChange({ method: value });
      // }
  };


  const attackMethods = {
      packet: [
          { value: 'syn-flood', label: 'SYN Flood' },
          { value: 'udp-flood', label: 'UDP Flood' },
          { value: 'ping-flood', label: 'Ping Flood (ICMP)' },
          { value: 'tcp-bypass', label: 'TCP Bypass' }, // Example
      ],
      bot: [
          { value: 'connection-flood', label: 'Connection Flood' },
          { value: 'chat-spam', label: 'Chat Spam / Flood' },
          { value: 'join-flood', label: 'Join/Leave Flood' },
          { value: 'lag-exploit', label: 'Lag Exploit Bot' }, // Example
      ]
  };

  // Determine button text and action based on state
  const getButtonAction = () => {
      if (isLoading) {
          return { text: 'Processing...', action: null, disabled: true, icon: 'fa-spinner fa-spin' };
      }
      if (isAttackRunning) {
          return { text: 'Stop DDoS Test', action: onStopAttack, disabled: isDisabled, icon: 'fa-stop' };
      }
      return { text: 'Start DDoS Test', action: onStartAttack, disabled: isDisabled, icon: 'fa-radiation' };
  };

  const buttonInfo = getButtonAction();
  const maxThreads = 512; // Define max threads for clarity

  return (
      <div className={`bg-dark-600 rounded-2xl p-6 gradient-bg-purple h-full flex flex-col transform-gpu hover:scale-[1.01] transition-medium border border-dark-400 hover:border-accent-purple/30 ${isDisabled && !isAttackRunning ? 'opacity-60 pointer-events-none' : ''}`}>
          {/* Background glow effects */}
          <div className="absolute -right-10 -top-10 w-32 h-32 bg-accent-purple opacity-5 rounded-full blur-xl"></div>
          <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-accent-pink opacity-5 rounded-full blur-xl"></div>

          <h3 className="font-bold text-lg mb-5 flex items-center group relative z-10">
              <div className="w-8 h-8 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium">
              <i className="fas fa-sliders-h text-accent-purple"></i>
              </div>
              Attack Configuration
          </h3>

          <div className="flex-1 space-y-6 relative z-10">
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                  <h4 className="text-sm font-semibold mb-3 text-white/90 flex items-center">
                      <i className={`fas fa-${attackConfig.type === 'packet' ? 'network-wired' : 'robot'} mr-2 text-accent-purple`}></i>
                      {attackConfig.type === 'packet' ? 'Packet Attack Method' : 'Bot Attack Method'}
                  </h4>
                  <select
                      className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium disabled:opacity-70"
                      value={localConfig.method}
                      onChange={(e) => {
                          handleInputChange('method', e.target.value);
                          onConfigChange({ method: e.target.value }); // Update parent immediately on select change
                      }}
                      disabled={isAttackRunning || isLoading || isDisabled}
                  >
                      {attackMethods[attackConfig.type].map(method => (
                          <option key={method.value} value={method.value}>{method.label}</option>
                      ))}
                  </select>
              </div>

              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                  <label className="block text-xs text-gray-400 mb-1">Attack Intensity: {localConfig.intensity}%</label>
                  <input
                      type="range"
                      min="1"
                      max="100"
                      value={localConfig.intensity}
                      onChange={(e) => handleInputChange('intensity', e.target.value)}
                      onMouseUp={(e) => onConfigChange({ intensity: parseInt(e.target.value, 10) })} // Update on release
                      onTouchEnd={(e) => onConfigChange({ intensity: parseInt(e.target.value, 10) })} // Update on touch end
                      className="w-full h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer hover:h-3 transition-medium disabled:opacity-70"
                      disabled={isAttackRunning || isLoading || isDisabled}
                  />
                   <div className="flex justify-between text-xs mt-1">
                       <span className="hover:text-accent-purple transition-medium">Low</span>
                       <span className="text-accent-purple hover:scale-110 transition-medium">Medium ({localConfig.intensity}%)</span>
                       <span className="hover:text-accent-purple transition-medium">High</span>
                   </div>
              </div>

              {/* === MODIFIED THREAD COUNT FIELD === */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                  <label className="block text-xs text-gray-400 mb-1">Thread Count: {localConfig.threads}</label>
                  <input
                      type="range" // Changed from "number" to "range"
                      min="1"
                      max={maxThreads} // Use defined maxThreads
                      value={localConfig.threads}
                      onChange={(e) => handleInputChange('threads', e.target.value)}
                      onMouseUp={(e) => onConfigChange({ threads: parseInt(e.target.value, 10) })} // Update on release
                      onTouchEnd={(e) => onConfigChange({ threads: parseInt(e.target.value, 10) })} // Update on touch end
                      className="w-full h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer hover:h-3 transition-medium disabled:opacity-70"
                      disabled={isAttackRunning || isLoading || isDisabled}
                  />
                  <div className="flex justify-between text-xs mt-1">
                       <span className="hover:text-accent-purple transition-medium">1</span>
                       <span className="text-accent-purple hover:scale-110 transition-medium">Value: {localConfig.threads}</span>
                       <span className="hover:text-accent-purple transition-medium">{maxThreads}</span>
                   </div>
              </div>
              {/* === END OF MODIFIED THREAD COUNT FIELD === */}

              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                   <label className="block text-xs text-gray-400 mb-1">Duration</label>
                   <div className="grid grid-cols-2 gap-3">
                     <input
                       type="number"
                       value={localConfig.duration.value}
                       onChange={(e) => handleDurationChange('value', e.target.value)}
                       onBlur={(e) => handleBlur('durationValue', e.target.value)}
                       className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium disabled:opacity-70"
                       disabled={isAttackRunning || isLoading || isDisabled}
                       min="1"
                     />
                     <select
                       className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium disabled:opacity-70"
                       value={localConfig.duration.unit}
                       onChange={(e) => {
                           handleDurationChange('unit', e.target.value);
                           onConfigChange({ duration: { ...attackConfig.duration, unit: e.target.value } }); // Update parent immediately
                       }}
                       disabled={isAttackRunning || isLoading || isDisabled}
                     >
                       <option value="seconds">Seconds</option>
                       <option value="minutes">Minutes</option>
                       <option value="hours">Hours</option>
                     </select>
                   </div>
               </div>

              {/* Advanced Options Toggle (optional) */}
              <div className="pt-4 border-t border-dark-400 transform-gpu transition-medium hover:scale-[1.02]">
                 <div className="flex items-center mb-3 group">
                   <input
                     type="checkbox"
                     id="advanced-options"
                     className="mr-2 transform-gpu group-hover:scale-110 transition-medium accent-accent-purple"
                     onChange={(e) => setShowAdvanced(e.target.checked)}
                     disabled={isAttackRunning || isLoading || isDisabled}
                   />
                   <label htmlFor="advanced-options" className="text-sm group-hover:text-accent-purple transition-medium">Show Advanced Options</label>
                 </div>
               </div>
               {/* TODO: Add Advanced Options fields when checkbox is checked */}
               {showAdvanced && (
                    <div className="space-y-4 p-4 bg-dark-500/50 rounded-lg border border-dark-400">
                        <p className="text-sm text-gray-300">Advanced options will appear here.</p>
                        {/* Example advanced option (placeholder) */}
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Custom Packet Size (Bytes)</label>
                            <input
                                type="number"
                                // value={localConfig.customPacketSize || ''}
                                // onChange={(e) => handleInputChange('customPacketSize', e.target.value)}
                                // onBlur={(e) => onConfigChange({ customPacketSize: parseInt(e.target.value, 10) || undefined })}
                                className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium disabled:opacity-70"
                                disabled={isAttackRunning || isLoading || isDisabled}
                                placeholder="e.g., 64"
                            />
                        </div>
                    </div>
                )}

          </div>

          {/* Start/Stop Button */}
          <button
              onClick={buttonInfo.action}
              disabled={buttonInfo.disabled}
              className={`w-full py-3 mt-6 rounded-xl transition font-medium relative z-10 transform-gpu hover:-translate-y-0.5 active:translate-y-0 active:shadow-inner flex items-center justify-center
                  ${isLoading ? 'bg-dark-400 text-gray-400 cursor-wait' : ''}
                  ${isAttackRunning && !isLoading ? 'bg-gradient-to-r from-accent-red to-accent-orange text-white hover:shadow-glow-pink' : ''}
                  ${!isAttackRunning && !isLoading ? 'bg-gradient-to-r from-accent-purple to-accent-pink text-white hover:shadow-glow-pink' : ''}
                  ${buttonInfo.disabled && !isLoading ? 'opacity-60 cursor-not-allowed hover:shadow-none hover:translate-y-0' : ''}
              `}
          >
              <i className={`fas ${buttonInfo.icon} mr-2 ${!isLoading && isAttackRunning ? 'group-hover:animate-pulse-slow' : ''} ${isLoading ? '' : 'group-hover:scale-110 transition-transform'}`}></i>
              {buttonInfo.text}
          </button>
      </div>
  );
};

export default AttackConfiguration;